import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import {
  deleteUserAsync,
  fetchUserByIdAsync,
} from "../../../../redux/features/adminSlice";
import { RootState } from "../../../../redux/app/store";
import moment from "moment";
import {
  ArrowLeft,
  Star,
  Globe,
  ExternalLink,
  Trash2,
} from "lucide-react";
import ConfirmDeleteUserModal from "../components/ConfirmDeleteUserModal";
import { isOppAdmin } from "../../../../helpers";

const BuyerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedUser, isLoading } = useAppSelector(
    (state: RootState) => state.admin,
  );
  const { user: authUser } = useAppSelector((state) => state.auth);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch full user profile on mount
  useEffect(() => {
    if (id) {
      setIsFetchingProfile(true);
      dispatch(fetchUserByIdAsync(id)).finally(() =>
        setIsFetchingProfile(false),
      );
    }
  }, [id, dispatch]);

  const buyer = selectedUser;

  const handleDelete = async () => {
    if (!buyer?.id) return;
    try {
      await dispatch(deleteUserAsync(buyer.id)).unwrap();
      setShowDeleteModal(false);
      navigate("/user-management/buyer");
    } catch (error) {
      // Error toast handled in thunk; keep the modal open so the admin can retry.
    }
  };

  // Skeleton placeholder component
  const SkeletonBlock = ({ className = "" }: { className?: string }) => (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
  );

  if (isFetchingProfile) {
    return (
      <div className="flex flex-col gap-6 md:p-8 p-4 bg-gray-100 min-h-screen">
        <div className="flex items-center gap-4">
          <button
            className="text-gray-500 hover:text-gray-800 flex items-center font-bold bg-gray-50 p-2 rounded"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <SkeletonBlock className="h-7 w-44" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card Skeleton */}
          <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center">
            <SkeletonBlock className="w-24 h-24 rounded-full mb-4" />
            <SkeletonBlock className="h-5 w-36 mb-2" />
            <SkeletonBlock className="h-4 w-28 mb-3" />
            <SkeletonBlock className="h-6 w-24 rounded-full mb-2" />
            <SkeletonBlock className="h-6 w-28 rounded-full mb-2" />
            <div className="mt-3 w-full flex flex-col gap-2">
              <SkeletonBlock className="h-10 w-full rounded-lg" />
              <SkeletonBlock className="h-10 w-full rounded-lg" />
            </div>
          </div>
          {/* Details Skeleton */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {[1, 2].map((section) => (
              <div key={section} className="bg-white rounded-2xl p-6 shadow-sm">
                <SkeletonBlock className="h-5 w-40 mb-4" />
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <div key={row} className="flex items-center py-3 border-b border-gray-100">
                    <SkeletonBlock className="h-4 w-32 mr-6" />
                    <SkeletonBlock className="h-4 w-48" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!buyer) {
    return (
      <div className="flex flex-col gap-6 md:p-8 p-4 bg-gray-100 min-h-screen">
        <button
          className="text-gray-500 hover:text-gray-800 flex items-center font-bold max-w-24 space-x-2 mb-4 bg-gray-50 p-2 rounded"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500 text-lg">No buyer data found.</p>
        </div>
      </div>
    );
  }

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => (
    <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-100">
      <span className="text-sm text-gray-500 font-medium sm:w-48 min-w-[12rem]">
        {label}
      </span>
      <span className="text-sm text-gray-900 mt-1 sm:mt-0">
        {value || "N/A"}
      </span>
    </div>
  );

  const StatusBadge = ({ value, trueText = "Yes", falseText = "No" }: { value: boolean | null | undefined; trueText?: string; falseText?: string }) => (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
        value ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {value ? trueText : falseText}
    </span>
  );

  return (
    <div className="flex flex-col gap-6 md:p-8 p-4 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            className="text-gray-500 hover:text-gray-800 flex items-center font-bold bg-gray-50 p-2 rounded"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <h1 className="text-lg md:text-2xl font-bold">Buyer Details</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col items-center">
          {buyer?.profileImage ? (
            <img
              src={buyer.profileImage}
              alt={buyer?.fullName || "Buyer"}
              className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-blue-200"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center text-3xl font-bold text-blue-600 mb-4">
              {buyer?.fullName?.charAt(0)?.toUpperCase() ||
                buyer?.firstName?.charAt(0)?.toUpperCase() ||
                "B"}
            </div>
          )}
          <h2 className="text-lg font-semibold text-gray-900">
            {buyer?.fullName ||
              `${buyer?.firstName ?? ""} ${buyer?.lastName ?? ""}`.trim() ||
              "N/A"}
          </h2>
          <p className="text-sm text-gray-500">@{buyer?.username || "N/A"}</p>
          <span className="mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
            Status: {buyer?.status || "N/A"}
          </span>

          {buyer?.userType && (
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Account type: {buyer?.userType}
            </span>
          )}

          {buyer?.kycStatus && (
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
              KYC: {buyer?.kycStatus}
            </span>
          )}

          {buyer?.isAmbassador && (
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              Ambassador
            </span>
          )}

          {(buyer?.averageRating != null || buyer?.buyerCount != null) && (
            <div className="mt-3 w-full flex flex-col gap-2">
              {buyer?.averageRating != null && (
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 text-yellow-500" /> Avg. Rating
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {buyer.averageRating}
                  </span>
                </div>
              )}
              {buyer?.buyerCount != null && (
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Buyer Count</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {buyer.buyerCount}
                  </span>
                </div>
              )}
            </div>
          )}

          {!isOppAdmin(authUser) && (
          <div className="mt-6 w-full pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={isLoading || !buyer?.id}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-red-600 hover:bg-red-700 text-white ${
                isLoading || !buyer?.id ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Trash2 className="w-4 h-4" /> Delete Buyer
            </button>
          </div>
          )}
        </div>

        {/* Details Cards */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Personal Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Personal Information
            </h3>
            <InfoRow
              label="Full Name"
              value={
                buyer?.fullName ||
                `${buyer?.firstName ?? ""} ${buyer?.lastName ?? ""}`.trim()
              }
            />
            <InfoRow label="Username" value={buyer?.username} />
            <InfoRow label="Email" value={buyer?.email} />
            <InfoRow label="Other Name" value={buyer?.othername} />
            <InfoRow label="Gender" value={buyer?.gender} />
            <InfoRow label="Phone Number" value={buyer?.phoneNumber} />
            <InfoRow
              label="Date of Birth"
              value={
                buyer?.dob
                  ? moment(buyer.dob).format("MMMM DD, YYYY")
                  : null
              }
            />
            <InfoRow
              label="City"
              value={
                typeof buyer?.city === "string"
                  ? buyer?.city
                  : buyer?.city?.name
              }
            />
            <InfoRow label="State" value={buyer?.state} />
            <InfoRow label="Address" value={buyer?.address} />
            <InfoRow label="Shipping Address" value={buyer?.shippingAddress} />
            <InfoRow label="Language" value={buyer?.language} />
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Account Information
            </h3>
            <InfoRow label="User Type" value={buyer?.userType} />
            <InfoRow label="Roles" value={buyer?.roles} />
            <InfoRow label="KYC Status" value={buyer?.kycStatus} />
            <InfoRow label="Referrer Code" value={buyer?.referrerCode} />
            <InfoRow
              label="Ambassador"
              value={<StatusBadge value={buyer?.isAmbassador} />}
            />
            <InfoRow
              label="Email Verified"
              value={<StatusBadge value={buyer?.emailVerified} />}
            />
            <InfoRow
              label="Phone Verified"
              value={<StatusBadge value={buyer?.phoneVerified} />}
            />
            <InfoRow
              label="2FA Enabled"
              value={<StatusBadge value={buyer?.twoFaEnabled} />}
            />
            <InfoRow
              label="Last Login"
              value={
                buyer?.lastLogin
                  ? moment(buyer.lastLogin).format("MMMM DD, YYYY [at] h:mm A")
                  : null
              }
            />
            <InfoRow
              label="Registered"
              value={
                buyer?.createdAt
                  ? moment(buyer.createdAt).format("MMMM DD, YYYY [at] h:mm A")
                  : null
              }
            />
            <InfoRow
              label="Last Updated"
              value={
                buyer?.updatedAt
                  ? moment(buyer.updatedAt).format("MMMM DD, YYYY [at] h:mm A")
                  : null
              }
            />
            {buyer?.deactivationReason && (
              <InfoRow label="Deactivation Reason" value={buyer.deactivationReason} />
            )}
            {buyer?.deactivationDate && (
              <InfoRow
                label="Deactivation Date"
                value={moment(buyer.deactivationDate).format("MMMM DD, YYYY [at] h:mm A")}
              />
            )}
            {buyer?.reactivationDate && (
              <InfoRow
                label="Reactivation Date"
                value={moment(buyer.reactivationDate).format("MMMM DD, YYYY [at] h:mm A")}
              />
            )}
          </div>

          {/* Wallet */}
          {buyer?.wallet && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Wallet
              </h3>
              <InfoRow label="Wallet Status" value={buyer.wallet.status} />
              <InfoRow label="Withdrawable Balance" value={buyer.wallet.withdrawableBalance != null ? `₦${Number(buyer.wallet.withdrawableBalance).toLocaleString()}` : null} />
              <InfoRow label="Tied Balance" value={buyer.wallet.tiedBalance != null ? `₦${Number(buyer.wallet.tiedBalance).toLocaleString()}` : null} />
              <InfoRow label="Pending Withdrawal" value={buyer.wallet.pendingWithdrawalBalance != null ? `₦${Number(buyer.wallet.pendingWithdrawalBalance).toLocaleString()}` : null} />
              <InfoRow label="Total Withdrawal" value={buyer.wallet.totalWithdrawal != null ? `₦${Number(buyer.wallet.totalWithdrawal).toLocaleString()}` : null} />
              <InfoRow label="Total Deposit" value={buyer.wallet.totalDeposit != null ? `₦${Number(buyer.wallet.totalDeposit).toLocaleString()}` : null} />
            </div>
          )}

          {/* KYC Record */}
          {buyer?.kycRecord && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                KYC Record
              </h3>
              <InfoRow
                label="Phone Verified"
                value={<StatusBadge value={buyer.kycRecord.phoneVerified} />}
              />
              <InfoRow
                label="Address Verified"
                value={<StatusBadge value={buyer.kycRecord.addressVerified} />}
              />
              <InfoRow
                label="BVN Verified"
                value={<StatusBadge value={buyer.kycRecord.bvnVerified} />}
              />
              <InfoRow
                label="NIN Verified"
                value={<StatusBadge value={buyer.kycRecord.ninVerified} />}
              />
              <InfoRow
                label="CAC Verified"
                value={<StatusBadge value={buyer.kycRecord.cacVerified} />}
              />
              <InfoRow label="Daily Spent" value={buyer.kycRecord.dailySpent != null ? `₦${Number(buyer.kycRecord.dailySpent).toLocaleString()}` : null} />
              <InfoRow label="Monthly Spent" value={buyer.kycRecord.monthlySpent != null ? `₦${Number(buyer.kycRecord.monthlySpent).toLocaleString()}` : null} />
            </div>
          )}

          {/* Bank Accounts */}
          {buyer?.bankAccounts && buyer.bankAccounts.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Bank Accounts
              </h3>
              {buyer.bankAccounts.map((bank: any, index: number) => (
                <div key={index} className={index > 0 ? "mt-4 pt-4 border-t border-gray-200" : ""}>
                  <InfoRow label="Account Name" value={bank?.accountName} />
                  <InfoRow label="Account Number" value={bank?.accountNumber} />
                  <InfoRow label="Bank Name" value={bank?.bankName} />
                  <InfoRow
                    label="Primary"
                    value={<StatusBadge value={bank?.isPrimary} />}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Business Information */}
          {buyer?.business && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Business Information
              </h3>
              <InfoRow label="Business Name" value={buyer?.business?.name} />
              <InfoRow
                label="Business Email"
                value={buyer?.business?.businessEmail}
              />
              <InfoRow
                label="Business Phone"
                value={buyer?.business?.businessPhoneNumber}
              />
              <InfoRow
                label="Business City"
                value={buyer?.business?.businessCity}
              />
              {buyer?.business?.businessLogo && (
                <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500 font-medium sm:w-48 min-w-[12rem]">
                    Business Logo
                  </span>
                  <img
                    src={buyer.business.businessLogo}
                    alt="Business Logo"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              )}
              <InfoRow
                label="Websites"
                value={
                  buyer?.business?.websites &&
                  (Array.isArray(buyer.business.websites)
                    ? buyer.business.websites.length > 0
                    : buyer.business.websites) ? (
                    <div className="flex flex-col gap-1">
                      {(Array.isArray(buyer.business.websites)
                        ? buyer.business.websites
                        : [buyer.business.websites]
                      ).map((url: string, i: number) => (
                        <a
                          key={i}
                          href={url.startsWith("http") ? url : `https://${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1 text-sm"
                        >
                          <Globe className="w-3.5 h-3.5" /> {url}
                        </a>
                      ))}
                    </div>
                  ) : (
                    "N/A"
                  )
                }
              />
            </div>
          )}

          {/* Socials */}
          {buyer?.socials && buyer.socials.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Social Accounts
              </h3>
              {buyer.socials.map((social: any, index: number) => (
                <InfoRow
                  key={index}
                  label={social?.name || `Social ${index + 1}`}
                  value={
                    social?.url ? (
                      <a
                        href={
                          social.url.startsWith("http")
                            ? social.url
                            : `https://${social.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> {social.url}
                      </a>
                    ) : (
                      "N/A"
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={isLoading}
        userName={
          buyer?.fullName ||
          `${buyer?.firstName ?? ""} ${buyer?.lastName ?? ""}`.trim() ||
          buyer?.username
        }
        userEmail={buyer?.email}
        usernameToConfirm={buyer?.username}
        userTypeLabel="Buyer"
      />

    </div>
  );
};

export default BuyerDetails;
